import { useState, useEffect, useCallback } from "react";
import api from "@/services/api";
import { useBrand } from "@/contexts/BrandContext";
import {
  AlertTriangle, CheckCircle2, Sparkles, ChevronDown, ChevronUp,
  Loader2, RefreshCw, Filter, Target, Shield, Wand2,
  MessageSquare, Clock, Users, BookOpen, Flag, X, Check
} from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  DEFECT:        { color: "text-[#BA0517]",     bg: "bg-[#FCEBEC]",     border: "border-[#BA0517]/20",     label: "Defect" },
  ENHANCEMENT:   { color: "text-[#5E4DB2]",   bg: "bg-[#F1ECFF]",   border: "border-[#D8CEF7]",   label: "Enhancement" },
  USABILITY:     { color: "text-[#8C4B00]",   bg: "bg-[#FFF4E5]",   border: "border-[#FE9339]/30",   label: "Usability" },
  CONFIGURATION: { color: "text-[#0176D3]",    bg: "bg-[#EEF4FF]",    border: "border-[#0176D3]/20",    label: "Config" },
  DATA:          { color: "text-[#0176D3]",    bg: "bg-[#EEF4FF]",    border: "border-[#0176D3]/20",    label: "Data" },
  SECURITY:      { color: "text-[#BA0517]",    bg: "bg-[#FCEBEC]",    border: "border-[#BA0517]/20",    label: "Security" },
  AI_QUALITY:    { color: "text-pink-700",    bg: "bg-pink-50",    border: "border-pink-200",    label: "AI Quality" },
  BUSINESS_RULE: { color: "text-indigo-700",  bg: "bg-indigo-50",  border: "border-indigo-200",  label: "Business Rule" },
  TRAINING:      { color: "text-[#706E6B]",   bg: "bg-[#FAFAFA]/40",   border: "border-[#C9C9C9]",   label: "Training" },
  QUESTION:      { color: "text-sky-700",     bg: "bg-sky-50",     border: "border-sky-200",     label: "Question" },
  PARTNER_TRUST_DESIGN: { color: "text-[#6832B8]", bg: "bg-[#F6EEFE]", border: "border-[#6832B8]/20", label: "Partner Trust Design" },
};

const STATUS_META = {
  NEW:               { color: "text-[#706E6B]",   label: "New" },
  AI_CLASSIFIED:     { color: "text-[#5E4DB2]",  label: "AI Classified" },
  TRIAGED:           { color: "text-[#0176D3]",   label: "Triaged" },
  ACCEPTED:          { color: "text-[#2E844A]", label: "Accepted" },
  IMPLEMENTING:      { color: "text-[#0176D3]",    label: "Implementing" },
  READY_FOR_RETEST:  { color: "text-[#8C4B00]",   label: "Retest Needed" },
  RETESTED:          { color: "text-[#0176D3]",    label: "Retested" },
  CLOSED:            { color: "text-[#706E6B]",   label: "Closed" },
  DEFERRED:          { color: "text-[#706E6B]",   label: "Deferred" },
  REJECTED:          { color: "text-[#BA0517]",     label: "Rejected" },
  NEEDS_CLARIFICATION: { color: "text-[#8C4B00]", label: "Needs Clarification" },
};

const PRIORITY_COLOR = {
  P0: "text-[#BA0517] bg-[#FCEBEC]",
  P1: "text-[#8C4B00] bg-[#FFF4E5]",
  P2: "text-[#8C4B00] bg-[#FFF4E5]",
  P3: "text-[#706E6B] bg-[#FAFAFA]",
  UAT_CRITICAL: "text-[#BA0517] bg-[#FCEBEC]",
  HIGH: "text-[#8C4B00] bg-[#FFF4E5]",
  NORMAL: "text-[#706E6B] bg-[#FAFAFA]",
  FUTURE: "text-[#706E6B] bg-white",
};

const LANG_FLAGS = { te: "TE", hi: "HI", ta: "TA", kn: "KN", ml: "ML", mr: "MR" };

const NEXT_ACTIONS = {
  NEW:               [{ status: "TRIAGED", label: "Triage" }, { status: "NEEDS_CLARIFICATION", label: "Needs Clarification" }],
  AI_CLASSIFIED:     [{ status: "TRIAGED", label: "Triage" }, { status: "NEEDS_CLARIFICATION", label: "Needs Clarification" }],
  TRIAGED:           [
    { status: "ACCEPTED", label: "Accept" },
    { status: "DEFERRED", label: "Defer" },
    { status: "REJECTED", label: "Reject" },
    { status: "NEEDS_CLARIFICATION", label: "Needs Clarification" },
  ],
  ACCEPTED:          [{ status: "IMPLEMENTING", label: "Start Implementing" }],
  IMPLEMENTING:      [{ status: "READY_FOR_RETEST", label: "Ready for Retest" }],
  READY_FOR_RETEST:  [
    { status: "RETESTED", label: "Retest Pass", extra: { retest_status: "PASS" } },
    { status: "IMPLEMENTING", label: "Retest Fail", extra: { retest_status: "FAIL" } },
  ],
  RETESTED:          [{ status: "CLOSED", label: "Close" }],
  DEFERRED:          [{ status: "TRIAGED", label: "Reopen" }],
  REJECTED:          [{ status: "TRIAGED", label: "Reopen" }],
  NEEDS_CLARIFICATION: [{ status: "TRIAGED", label: "Mark Triaged" }],
};

const SIGNOFF_LABELS = {
  PRASHASTA_BUSINESS: "Prashasta — Business",
  PRASHASTA_OPERATIONS: "Prashasta — Operations",
  ARGOV_PRODUCT: "Platform — Product",
  ARGOV_TECHNICAL: "Platform — Technical",
};

export default function UATControlCenter() {
  const brand = useBrand();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [gate, setGate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ type: "ALL", status: "ALL", priority: "ALL", module: "ALL" });
  const [search, setSearch] = useState("");
  const [showGate, setShowGate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.type !== "ALL") params.set("feedback_type", filters.type);
      if (filters.status !== "ALL") params.set("status", filters.status);
      if (filters.priority !== "ALL") params.set("priority", filters.priority);
      if (filters.module !== "ALL") params.set("module", filters.module);
      if (search) params.set("search", search);
      const [feedRes, statsRes, gateRes] = await Promise.all([
        api.get(`/uat/feedback?${params}`),
        api.get("/uat/dashboard"),
        api.get("/uat/closure-gate"),
      ]);
      setItems(feedRes.data.feedback || []);
      setStats(statsRes.data);
      setGate(gateRes.data);
    } catch (e) {
      toast.error("Failed to load UAT data");
    } finally {
      setLoading(false);
    }
  }, [filters, search]);

  useEffect(() => { load(); }, [load]);

  async function handleAction(itemId, status, extra = {}) {
    try {
      await api.patch(`/uat/feedback/${itemId}`, { status, ...extra });
      toast.success(`Status updated: ${STATUS_META[status]?.label || status}`);
      load();
      if (selected?.id === itemId) setSelected(prev => ({ ...prev, status, ...extra }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  }

  async function handleSignoff(role) {
    const name = prompt(`Enter your name for sign-off as ${SIGNOFF_LABELS[role]}:`);
    if (!name?.trim()) return;
    try {
      await api.post("/uat/closure-gate/signoff", { role, signed_by_name: name.trim(), notes: "" });
      toast.success("Sign-off recorded");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sign-off failed");
    }
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-5 h-5 text-[#0176D3]" />
            <h1 className="text-2xl font-bold text-[#181818]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              UAT Control Center
            </h1>
          </div>
          <p className="text-[#706E6B] text-xs">{brand.productName} — UAT Release 1 · Insight Agent powered by Sarvam AI</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowGate(!showGate)}
            data-testid="uat-closure-gate-btn"
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
              gate?.status === "FORMALLY_CLOSED"
                ? "bg-[#EAF5EE] border-[#2E844A]/30 text-[#2E844A]"
                : "bg-white border-[#C9C9C9] text-[#706E6B] hover:text-[#181818]"
            }`}
          >
            <Shield className="w-3.5 h-3.5" />
            Closure Gate: {gate?.status === "FORMALLY_CLOSED" ? "READY" : "NOT READY"}
          </button>
          <button onClick={load} className="p-2 text-[#706E6B] hover:text-[#181818] border border-[#C9C9C9] rounded-lg">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          <StatCard label="Total" value={stats.total} color="text-[#181818]" />
          <StatCard label="Defects" value={stats.by_type?.DEFECT || 0} color="text-[#BA0517]" />
          <StatCard label="Enhancements" value={stats.by_type?.ENHANCEMENT || 0} color="text-[#5E4DB2]" />
          <StatCard label="Security" value={stats.by_type?.SECURITY || 0} color="text-rose-400" />
          <StatCard label="Untriaged" value={stats.untriaged} color={stats.untriaged > 0 ? "text-[#8C4B00]" : "text-[#706E6B]"} />
          <StatCard label="Open P0" value={stats.open_p0} color={stats.open_p0 > 0 ? "text-[#BA0517] font-bold" : "text-[#706E6B]"} />
          <StatCard label="Open P1" value={stats.open_p1} color={stats.open_p1 > 0 ? "text-[#8C4B00]" : "text-[#706E6B]"} />
          <StatCard label="Retest Needed" value={stats.retest_pending} color={stats.retest_pending > 0 ? "text-[#8C4B00]" : "text-[#706E6B]"} />
        </div>
      )}

      {/* Closure Gate */}
      {showGate && gate && (
        <ClosureGatePanel gate={gate} onSignoff={handleSignoff} onCollapse={() => setShowGate(false)} />
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <Filter className="w-3.5 h-3.5 text-[#706E6B]" />
        {[
          { key: "type", options: ["ALL", "DEFECT", "ENHANCEMENT", "USABILITY", "SECURITY", "AI_QUALITY", "CONFIGURATION", "DATA", "BUSINESS_RULE", "TRAINING", "QUESTION", "PARTNER_TRUST_DESIGN"] },
          { key: "status", options: ["ALL", "NEW", "AI_CLASSIFIED", "TRIAGED", "ACCEPTED", "IMPLEMENTING", "READY_FOR_RETEST", "RETESTED", "CLOSED", "DEFERRED", "REJECTED"] },
          { key: "priority", options: ["ALL", "P0", "P1", "P2", "P3"] },
          { key: "module", options: ["ALL", "ONE Engage", "ONE Imagine", "ONE Threads", "Event 360", "Setup Studio", "Administration", "General"] },
        ].map(({ key, options }) => (
          <select
            key={key}
            value={filters[key]}
            onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}
            className="bg-[#F3F3F3] border border-[#E5E5E5] text-[#444444] text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#0176D3]/30"
          >
            {options.map(o => (
              <option key={o} value={o}>{key === "type" ? (TYPE_META[o]?.label || o) : o}</option>
            ))}
          </select>
        ))}
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search feedback…"
          className="bg-[#F3F3F3] border border-[#E5E5E5] text-[#444444] text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#0176D3]/30 w-40"
        />
      </div>

      {/* Main Content */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-[#0176D3]" />
        </div>
      ) : (
        <div className="flex gap-4">
          {/* Feedback List */}
          <div className="flex-1 space-y-2 min-w-0">
            {items.length === 0 ? (
              <div className="bg-white border border-[#E5E5E5] rounded-xl p-12 text-center">
                <MessageSquare className="w-10 h-10 text-[#444444] mx-auto mb-3" />
                <p className="text-[#706E6B] text-sm">No feedback items match your filters.</p>
              </div>
            ) : (
              items.map(item => (
                <FeedbackCard
                  key={item.id}
                  item={item}
                  selected={selected?.id === item.id}
                  onClick={() => setSelected(selected?.id === item.id ? null : item)}
                  onAction={handleAction}
                />
              ))
            )}
          </div>

          {/* Detail Panel */}
          {selected && (
            <div className="hidden lg:block w-80 flex-shrink-0">
              <FeedbackDetailPanel
                item={selected}
                onAction={handleAction}
                onClose={() => setSelected(null)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Feedback Card ─────────────────────────────────────────────────────────────
function FeedbackCard({ item, selected, onClick, onAction }) {
  const typeMeta = TYPE_META[item.feedback_type] || TYPE_META.QUESTION;
  const statusMeta = STATUS_META[item.status] || {};
  const actions = NEXT_ACTIONS[item.status] || [];

  return (
    <div
      data-testid={`uat-item-${item.id}`}
      className={`bg-white border rounded-xl p-4 cursor-pointer transition-all ${
        selected ? "border-[#0176D3]/30 shadow-[0_0_0_1px_rgba(56,189,248,0.2)]" : "border-[#E5E5E5] hover:border-[#C9C9C9]"
      }`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 w-8 h-8 rounded-lg ${typeMeta.bg} ${typeMeta.border} border flex items-center justify-center`}>
          <span className={`text-[10px] font-bold ${typeMeta.color}`}>
            {item.feedback_type?.[0] || "?"}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] font-mono text-[#706E6B]">{item.feedback_number}</span>
            <TypeBadge type={item.feedback_type} />
            <PriorityBadge priority={item.suggested_priority} />
            <span className={`text-[10px] font-medium ${statusMeta.color}`}>{statusMeta.label}</span>
            {item.detected_language && item.detected_language !== "en" && (
              <span className="text-[9px] px-1.5 py-0.5 bg-[#F1ECFF] text-[#5E4DB2] rounded font-mono">
                {LANG_FLAGS[item.detected_language] || item.detected_language.toUpperCase()}
              </span>
            )}
          </div>
          <p className="text-sm text-[#181818] leading-relaxed mb-1">
            {item.normalized_interpretation || item.original_text}
          </p>
          {item.normalized_interpretation && item.detected_language !== "en" && (
            <p className="text-xs text-[#706E6B] italic mb-1 truncate">
              Original: "{item.original_text}"
            </p>
          )}
          <div className="flex items-center gap-3 flex-wrap mt-2">
            <span className="text-[10px] text-[#706E6B]">{item.module}</span>
            {item.feature && <span className="text-[10px] text-[#706E6B]">· {item.feature}</span>}
            {item.screen && <span className="text-[10px] text-[#706E6B]">· {item.screen}</span>}
            {item.agent_confidence > 0 && (
              <span className="text-[10px] text-[#5E4DB2] flex items-center gap-0.5">
                <Wand2 className="w-2.5 h-2.5" />
                {Math.round(item.agent_confidence * 100)}%
              </span>
            )}
            {item.possible_duplicates?.length > 0 && (
              <span className="text-[9px] px-1.5 py-0.5 bg-[#FFF4E5] text-[#8C4B00] border border-[#FE9339]/30 rounded font-mono">
                {item.possible_duplicates.length} possible dup{item.possible_duplicates.length > 1 ? "s" : ""}
              </span>
            )}
          </div>
          {/* Quick actions */}
          {actions.length > 0 && (
            <div className="flex gap-2 mt-3 flex-wrap" onClick={e => e.stopPropagation()}>
              {actions.slice(0, 3).map(action => (
                <button
                  key={action.status}
                  data-testid={`uat-action-${item.id}-${action.status.toLowerCase()}`}
                  onClick={() => onAction(item.id, action.status, action.extra || {})}
                  className={`text-[10px] px-2.5 py-1 rounded-lg font-medium border transition-colors ${
                    action.status === "REJECTED" || action.status === "DEFERRED"
                      ? "text-[#706E6B] border-[#C9C9C9] hover:text-[#181818] hover:border-slate-500"
                      : action.status === "ACCEPTED" || action.status === "CLOSED"
                        ? "text-[#2E844A] border-[#2E844A]/20 bg-[#EAF5EE] hover:bg-[#EAF5EE]"
                        : "text-[#0176D3] border-[#0176D3]/30 bg-[#0176D3]/5 hover:bg-[#0176D3]/15"
                  }`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Detail Panel ──────────────────────────────────────────────────────────────
function FeedbackDetailPanel({ item, onAction, onClose }) {
  const typeMeta = TYPE_META[item.feedback_type] || TYPE_META.QUESTION;
  const actions = NEXT_ACTIONS[item.status] || [];

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-4 sticky top-4 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono text-[#706E6B]">{item.feedback_number}</span>
        <button onClick={onClose} className="p-1 hover:bg-[#FAFAFA] rounded text-[#706E6B]">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <p className="text-[10px] text-[#706E6B] uppercase tracking-wide mb-1">Original Feedback</p>
          <p className="text-sm text-[#444444] leading-relaxed bg-[#FAFAFA] rounded-lg px-3 py-2">
            {item.original_text}
          </p>
        </div>
        {item.normalized_interpretation && item.normalized_interpretation !== item.original_text && (
          <div>
            <p className="text-[10px] text-[#706E6B] uppercase tracking-wide mb-1 flex items-center gap-1">
              <Wand2 className="w-2.5 h-2.5 text-[#5E4DB2]" /> AI Normalized
            </p>
            <p className="text-sm text-[#181818] leading-relaxed bg-[#F1ECFF] border border-[#D8CEF7] rounded-lg px-3 py-2">
              {item.normalized_interpretation}
            </p>
          </div>
        )}
        {item.business_impact && (
          <div>
            <p className="text-[10px] text-[#706E6B] uppercase tracking-wide mb-1">Business Impact</p>
            <p className="text-xs text-[#706E6B] leading-relaxed">{item.business_impact}</p>
          </div>
        )}
        {item.possible_duplicates?.length > 0 && (
          <div className="p-2 bg-[#FFF4E5] border border-[#FE9339]/30 rounded-lg">
            <p className="text-[10px] text-[#8C4B00] font-semibold">Possible Duplicates</p>
            <p className="text-[10px] text-amber-600 mt-0.5">
              {item.possible_duplicates.join(", ")}
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          {[
            ["Type", <TypeBadge type={item.feedback_type} />],
            ["Priority", <PriorityBadge priority={item.suggested_priority} />],
            ["Module", item.module],
            ["Screen", item.screen || "—"],
            ["Language", item.detected_language?.toUpperCase() || "EN"],
            ["Status", STATUS_META[item.status]?.label || item.status],
          ].map(([label, value]) => (
            <div key={label}>
              <p className="text-[9px] text-[#706E6B] uppercase">{label}</p>
              <div className="text-xs text-[#444444] mt-0.5">{value}</div>
            </div>
          ))}
        </div>
        {(item.decision_reason || item.resolution) && (
          <div className="p-2 bg-[#FAFAFA] rounded-lg">
            {item.decision_reason && (
              <p className="text-xs text-[#706E6B]"><span className="text-[#706E6B]">Decision: </span>{item.decision_reason}</p>
            )}
            {item.resolution && (
              <p className="text-xs text-[#706E6B] mt-1"><span className="text-[#706E6B]">Resolution: </span>{item.resolution}</p>
            )}
          </div>
        )}
      </div>

      {actions.length > 0 && (
        <div className="space-y-2 border-t border-[#E5E5E5] pt-3">
          <p className="text-[10px] text-[#706E6B] uppercase">Actions</p>
          {actions.map(action => (
            <button
              key={action.status}
              onClick={() => onAction(item.id, action.status, action.extra || {})}
              className={`w-full py-2 rounded-lg text-xs font-medium transition-colors ${
                action.status === "ACCEPTED" || action.status === "CLOSED"
                  ? "bg-[#EAF5EE] text-[#2E844A] border border-[#2E844A]/20 hover:bg-[#EAF5EE]"
                  : action.status === "REJECTED"
                    ? "bg-[#FCEBEC] text-[#BA0517] border border-[#BA0517]/20 hover:bg-[#FCEBEC]"
                    : action.status === "DEFERRED"
                      ? "bg-[#FAFAFA] text-[#706E6B] border border-[#C9C9C9] hover:bg-[#E5E5E5]"
                      : "bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30 hover:bg-[#EEF4FF]"
              }`}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Closure Gate Panel ────────────────────────────────────────────────────────
function ClosureGatePanel({ gate, onSignoff, onCollapse }) {
  const allPass = gate.status === "FORMALLY_CLOSED";

  return (
    <div className={`border rounded-xl p-5 ${allPass ? "bg-emerald-950/20 border-[#2E844A]/20" : "bg-white border-[#E5E5E5]"}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-bold text-[#181818]">UAT Release 1 — Closure Gate</h2>
          <p className="text-xs text-[#706E6B] mt-0.5">Stage 04 ONE Commerce remains LOCKED until this gate passes</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold px-3 py-1 rounded-full border ${
            allPass
              ? "bg-[#EAF5EE] text-[#2E844A] border-[#2E844A]/30"
              : "bg-[#FAFAFA] text-[#706E6B] border-[#C9C9C9]"
          }`}>
            {allPass ? "FORMALLY CLOSED" : "NOT READY"}
          </span>
          <button onClick={onCollapse} className="p-1.5 hover:bg-[#FAFAFA] rounded text-[#706E6B]">
            <ChevronUp className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
        {Object.entries(gate.checks || {}).map(([key, pass]) => (
          <div key={key} className={`flex items-center gap-2 p-2.5 rounded-lg ${pass ? "bg-[#EAF5EE]" : "bg-[#FCEBEC]"}`}>
            {pass
              ? <CheckCircle2 className="w-4 h-4 text-[#2E844A] flex-shrink-0" />
              : <AlertTriangle className="w-4 h-4 text-[#BA0517] flex-shrink-0" />
            }
            <span className={`text-xs font-medium ${pass ? "text-[#2E844A]" : "text-red-300"}`}>
              {key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
            </span>
          </div>
        ))}
      </div>

      {/* Sign-offs */}
      <div className="border-t border-[#E5E5E5] pt-4">
        <p className="text-xs font-semibold text-[#706E6B] mb-3">Sign-offs Required</p>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(SIGNOFF_LABELS).map(([role, label]) => {
            const signed = gate.signed_by?.includes(role);
            return (
              <div key={role} className={`flex items-center justify-between p-2.5 rounded-lg border ${
                signed ? "bg-[#EAF5EE] border-[#2E844A]/20" : "bg-[#FAFAFA] border-[#C9C9C9]/50"
              }`}>
                <div>
                  <p className="text-[10px] font-medium text-[#444444]">{label}</p>
                  {signed && <p className="text-[9px] text-emerald-500">Signed</p>}
                </div>
                {!signed && (
                  <button
                    onClick={() => onSignoff(role)}
                    className="text-[9px] px-2 py-0.5 bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30 rounded font-medium hover:bg-[#EEF4FF] transition-colors"
                  >
                    Sign
                  </button>
                )}
                {signed && <CheckCircle2 className="w-3.5 h-3.5 text-[#2E844A]" />}
              </div>
            );
          })}
        </div>
      </div>

      {!allPass && (
        <div className="mt-4 p-3 bg-[#FAFAFA] rounded-lg">
          <p className="text-[10px] text-[#706E6B] font-mono">
            STAGE 04 ONE COMMERCE — LOCKED
          </p>
          <p className="text-[10px] text-[#706E6B] mt-0.5">
            P0: {gate.open_p0_count} open · P1: {gate.open_p1_count} open · Untriaged: {gate.untriaged_count} · Security: {gate.security_open_count} open
          </p>
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function StatCard({ label, value, color }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-3">
      <p className="text-[10px] text-[#706E6B] uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-xl font-bold ${color}`}>{value ?? 0}</p>
    </div>
  );
}

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || TYPE_META.QUESTION;
  return (
    <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium border ${meta.bg} ${meta.border} ${meta.color}`}>
      {meta.label}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const cls = PRIORITY_COLOR[priority] || "text-[#706E6B] bg-[#FAFAFA]";
  return (
    <span className={`text-[9px] px-2 py-0.5 rounded font-mono ${cls}`}>
      {priority}
    </span>
  );
}
